import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Gift, Copy, Check, X, Twitter, Linkedin, Mail } from "lucide-react";

/**
 * One-time celebratory modal shown right after a user finishes onboarding.
 * Prompts them to share their invite link and earn free months. Keyed by
 * a per-user localStorage flag so it never nags twice.
 *
 * Mounted at the top of AtlasMap. Reads /referrals/me once on show.
 */
const KEY = (userId) => `atlas-share-prompt-shown-${userId}`;

export default function PostSignupShareModal({ user }) {
    const [open, setOpen] = useState(false);
    const [ref, setRef] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!user || !user.id) return;
        // Only for users who just completed onboarding (has_access from trial/sub)
        if (localStorage.getItem(KEY(user.id))) return;
        // Small delay so it doesn't slam the user the instant they land
        const t = setTimeout(() => {
            api.get("/referrals/me")
                .then((r) => {
                    setRef(r.data);
                    setOpen(true);
                })
                .catch(() => {});
        }, 900);
        return () => clearTimeout(t);
    }, [user]);

    const close = () => {
        setOpen(false);
        if (user?.id) localStorage.setItem(KEY(user.id), "1");
    };

    const inviteUrl = ref?.code
        ? `${window.location.origin}/?ref=${ref.code}`
        : "";

    const shareText = "Join me on Project Atlas — the interactive map of every human discipline. First month's on us.";

    const copy = async () => {
        if (!inviteUrl) return;
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {}
    };

    return (
        <AnimatePresence>
            {open && ref?.code && (
                <motion.div
                    key="ps-share"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={close}
                    data-testid="post-signup-share-modal"
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ type: "spring", damping: 22 }}
                        onClick={(e) => e.stopPropagation()}
                        className="crystal rounded-3xl p-7 sm:p-8 w-full max-w-lg relative"
                    >
                        <button
                            onClick={close}
                            className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-900 hover:bg-white/60"
                            data-testid="post-signup-share-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-center gap-2">
                            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "#DBF3E9" }}>
                                <Gift className="w-5 h-5" color="#136A4A" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest font-bold text-emerald-800">Welcome to Atlas</p>
                                <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 leading-tight">
                                    Bring a friend, earn a free month.
                                </h2>
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                            Every friend who signs up with your link gives <b>both of you a free month</b> of Atlas. Stack them —
                            10 invites is 10 months of free access. Your unique link:
                        </p>

                        <div className="mt-4 flex flex-wrap gap-2 items-center">
                            <code className="font-mono text-xs sm:text-sm px-3 py-2.5 rounded-xl bg-white/80 border border-slate-200 flex-1 min-w-[220px] break-all" data-testid="post-signup-invite-url">
                                {inviteUrl}
                            </code>
                            <button
                                onClick={copy}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="post-signup-copy-btn"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(inviteUrl)}`}
                                target="_blank" rel="noreferrer"
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="post-signup-share-twitter"
                            >
                                <Twitter className="w-4 h-4" /> Post to X
                            </a>
                            <a
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(inviteUrl)}`}
                                target="_blank" rel="noreferrer"
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="post-signup-share-linkedin"
                            >
                                <Linkedin className="w-4 h-4" /> LinkedIn
                            </a>
                            <a
                                href={`mailto:?subject=${encodeURIComponent("Come explore Project Atlas with me")}&body=${encodeURIComponent(shareText + "\n\n" + inviteUrl)}`}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="post-signup-share-email"
                            >
                                <Mail className="w-4 h-4" /> Email
                            </a>
                        </div>

                        <div className="mt-6 pt-4 border-t border-white/60 flex items-center justify-between">
                            <span className="text-xs text-slate-500">You can grab this link anytime from your Profile.</span>
                            <button
                                onClick={close}
                                className="text-sm font-display font-bold text-slate-500 hover:text-slate-900"
                                data-testid="post-signup-later"
                            >
                                Maybe later →
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
